import strings from '../../../constant/strings';
import {
  SHOPIFY_API_VERSION, SHOPIFY_DOMAIN, SHOPIFY_STOREFRONT_TOKEN,
} from './utils';

/**
 * Everything shared by the shop and the product page: loading Shopify's Buy
 * Button library, the styling that makes its widgets look like the rest of the
 * app, and handing Shopify the patient's details so checkout arrives filled in.
 *
 * Shopify still owns the cart and the checkout. We only decide where its
 * widgets sit and what they look like.
 */
const BUY_BUTTON_SCRIPT = 'https://sdks.shopifycdn.com/buy-button/latest/buybutton.js';
const BRAND_GRADIENT = 'linear-gradient(90deg, #097EB7 0%, #2AAB6C 100%)';
const FONT = { 'font-family': 'Poppins, sans-serif' };
const POPPINS_HREF = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';

const BUYER_IDENTITY_MUTATION = `
  mutation Emed4uBuyerIdentity($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id }
      userErrors { field message }
    }
  }
`;

let scriptPromise = null;
export const loadBuyButtonScript = () => {
  if (window.ShopifyBuy && window.ShopifyBuy.UI) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = BUY_BUTTON_SCRIPT;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => { scriptPromise = null; reject(new Error('Shopify Buy Button script failed to load')); };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

export const buildClient = () => window.ShopifyBuy.buildClient({
  domain: SHOPIFY_DOMAIN,
  storefrontAccessToken: SHOPIFY_STOREFRONT_TOKEN,
});

// Shopify's ids are gids ("gid://shopify/Product/123"); its widgets want the
// numeric part.
export const numericId = (id) => String(id || '').split('/').pop();

const buttonStyle = {
  ...FONT,
  'font-weight': '500',
  'background': BRAND_GRADIENT,
  'border-radius': '8px',
  ':hover': { 'background': BRAND_GRADIENT, 'opacity': '0.9' },
  ':focus': { 'background': BRAND_GRADIENT },
};

// Each widget is its own iframe and does not inherit the app's font. The
// library's own Google Fonts loader is blocked by our Content Security Policy,
// so the stylesheet is added to each widget document once it has rendered.
const addPoppins = (component) => {
  try {
    const doc = component && component.view && component.view.document;
    if (!doc || !doc.head || doc.querySelector('link[data-emed4u-font]')) return;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = POPPINS_HREF;
    link.setAttribute('data-emed4u-font', 'poppins');
    doc.head.appendChild(link);
  } catch (e) { /* widget document not reachable; default font stays */ }
};
const fontEvents = { afterRender: addPoppins };

/**
 * The cart, its toggle and the line items. Shared by every page that shows a
 * Buy Button widget, so the floating cart looks and behaves the same
 * everywhere and keeps its contents as the patient moves between pages.
 */
export const cartOptions = (onCartEvent = () => {}) => ({
  cart: {
    events: {
      afterInit: (component) => { addPoppins(component); onCartEvent(component); },
      afterRender: (component) => { addPoppins(component); onCartEvent(component); },
    },
    styles: {
      cart: { ...FONT },
      title: { ...FONT, 'font-weight': '600', 'color': '#2B2B2B' },
      header: { ...FONT },
      lineItems: { ...FONT },
      subtotalText: { ...FONT },
      subtotal: { ...FONT },
      notice: { ...FONT },
      button: buttonStyle,
    },
    text: {
      title: strings.cart,
      total: strings.subtotal,
      button: strings.proceedToCheckout,
      empty: strings.cartEmpty,
      notice: strings.checkoutNote,
    },
    popup: false, // checkout opens in this tab so the order page can send the patient back
  },
  lineItem: {
    styles: { title: { ...FONT, 'color': '#2B2B2B' }, price: { ...FONT }, quantity: { ...FONT } },
  },
  toggle: {
    events: fontEvents,
    styles: {
      toggle: { ...FONT, 'background': BRAND_GRADIENT, 'border-radius': '8px 0 0 8px', ':hover': { 'background': BRAND_GRADIENT } },
      count: { ...FONT },
    },
  },
  modal: { events: fontEvents },
  moneyFormat: '₹{{amount}}',
});

/**
 * Adds a variant to the cart Shopify already owns, so our own product page can
 * drive the same cart the floating widget shows. `variant` is a variant object
 * from the SDK, not an id.
 */
export const addToCart = (ui, variant, quantity = 1) => {
  const cart = ui && ui.components && ui.components.cart && ui.components.cart[0];
  if (!cart || !variant) return Promise.reject(new Error('cart not ready'));
  return Promise.resolve(cart.addVariantToCart(variant, quantity));
};

/**
 * Buy now: add the item, then hand the patient straight to Shopify's checkout
 * instead of opening the cart.
 */
export const buyNow = async (ui, variant, quantity = 1) => {
  const cart = ui && ui.components && ui.components.cart && ui.components.cart[0];
  if (!cart || !variant) throw new Error('cart not ready');
  const updated = await Promise.resolve(cart.addVariantToCart(variant, quantity));
  const url = (updated && updated.checkoutUrl)
    || (cart.model && cart.model.checkoutUrl);
  if (!url) throw new Error('no checkout url');
  window.location.href = url;
  return url;
};

/**
 * Hands Shopify what the patient already told us - email, phone and name - so
 * its checkout arrives prefilled and the order is attributed to them. One call
 * per cart, because each buyer-identity update replaces the previous one.
 */
export const makeBuyerIdentityApplier = (buyerIdentity, identifiedCarts) => (component) => {
  const cartId = component && component.model && component.model.id;
  if (!buyerIdentity || !cartId || identifiedCarts.has(cartId)) return;
  identifiedCarts.add(cartId);
  fetch(`https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN },
    body: JSON.stringify({ query: BUYER_IDENTITY_MUTATION, variables: { cartId, buyerIdentity } }),
  })
    .then((res) => res.json())
    .then((body) => {
      const result = body && body.data && body.data.cartBuyerIdentityUpdate;
      if (!result || (result.userErrors && result.userErrors.length)) {
        identifiedCarts.delete(cartId); // a later cart event can retry
      }
    })
    .catch(() => { identifiedCarts.delete(cartId); });
};
